/* eslint-disable */

/**
 * Insurance Verification — shared constants & pure helpers (Slice 6a).
 *
 * Status → chip presentation, warning-code → chip presentation, the
 * Action-Required ordering, plus date-range / relative-time / carrier-avatar
 * helpers used across the dashboard components.
 *
 * Colors are literal hex values lifted from the design-system tokens
 * (`_ds/.../tokens/colors.css`) — we intentionally do NOT import the token CSS
 * into the app; we match the values by hand (teal-600 #0f7259, red-500
 * #e5484d, amber-500 #f59e0b, etc.).
 *
 * Field names come from the backend assembler
 * (`insurance-verification-service/lib/dashboard-assemble.js`): each row has
 * `verification.status` ∈ {verified, inactive, unable_to_verify, error,
 * pending, null} and `verification.warnings[] = { code, severity, title,
 * detail }`; warning codes are INACTIVE / UNABLE_TO_VERIFY / MAX_REACHED /
 * MAX_NEAR / WAITING_PERIOD.
 */

import 'moment-timezone';
import moment from 'moment-timezone';

// ── Palette (token hex mirror) ──────────────────────────────────────────────
export const COLORS = {
  teal600: '#0f7259',
  teal700: '#0b5c49',
  teal50: '#e9f6f1',
  teal100: '#cdede2',
  red500: '#e5484d',
  red50: '#fdeeee',
  amber500: '#f59e0b',
  amber50: '#fef6e7',
  amberText: '#b45309',
  blue500: '#3b82f6',
  blue50: '#eff5ff',
  neutral400: '#9ca4a2',
  neutral500: '#717977',
  neutral100: '#eceff0',
};

/**
 * Status → chip. `icon` is a FontAwesome-4 class (the app ships FA4). `key`
 * matches the backend `verification.status` string; `none` is our synthetic
 * bucket for an insured patient with no verification yet, `no_insurance` for a
 * row with no carrier at all.
 */
export const STATUS_MAP = {
  verified:          { label: 'Verified',           icon: 'fa-check-circle',      color: COLORS.teal600,    bg: COLORS.teal50 },
  inactive:          { label: 'Inactive insurance',  icon: 'fa-ban',               color: COLORS.red500,     bg: COLORS.red50 },
  unable_to_verify:  { label: 'Unable to verify',    icon: 'fa-times-circle',      color: COLORS.red500,     bg: COLORS.red50 },
  error:             { label: 'Error',               icon: 'fa-exclamation-circle',color: COLORS.red500,     bg: COLORS.red50 },
  pending:           { label: 'Pending',             icon: 'fa-clock-o',           color: COLORS.blue500,    bg: COLORS.blue50 },
  none:              { label: 'Not verified',        icon: 'fa-minus-circle',      color: COLORS.neutral500, bg: COLORS.neutral100 },
  no_insurance:      { label: 'No insurance',        icon: 'fa-minus',             color: COLORS.neutral400, bg: COLORS.neutral100 },
};

/**
 * Resolve the presentation bucket for a dashboard row. Insured-but-unverified
 * → `none`; uninsured → `no_insurance`; otherwise the status' own bucket.
 */
export function statusForRow(row) {
  if (!row) return STATUS_MAP.none;
  const ins = row.insurance || {};
  const status = (row.verification && row.verification.status) || null;
  if (!status) {
    return ins.hasInsurance === false ? STATUS_MAP.no_insurance : STATUS_MAP.none;
  }
  return STATUS_MAP[status] || { label: titleCase(status), icon: 'fa-question-circle', color: COLORS.neutral500, bg: COLORS.neutral100 };
}

/**
 * Warning-code / Action-Required chip presentation. `severity` drives the
 * red-vs-amber styling. Unknown codes fall back to a title-cased amber chip.
 */
export const CHIP_MAP = {
  INACTIVE:         { label: 'Inactive insurance',     severity: 'red',   icon: 'fa-ban' },
  UNABLE_TO_VERIFY: { label: 'Unable to verify',       severity: 'red',   icon: 'fa-times-circle' },

  // ── OD data issues (lib/preflight.js BLOCK_CODES) ────────────────────────
  // AMBER, not red, deliberately. The red chips above are a real answer we
  // can't argue with (the payer said the plan is inactive / couldn't be found).
  // These are fixable typos in the practice's OWN Open Dental record — someone
  // corrects the field and the check runs. They also cost NOTHING to hit: the
  // backend screens them BEFORE calling Stedi, so a blocked patient is a to-do
  // item, not a failure to alarm about.
  MISSING_MEMBER_ID:       { label: 'Missing member ID',       severity: 'amber', icon: 'fa-id-card-o' },
  PLACEHOLDER_MEMBER_ID:   { label: 'Placeholder member ID',   severity: 'amber', icon: 'fa-id-card-o' },
  MISSING_SUBSCRIBER_NAME: { label: 'Missing subscriber name', severity: 'amber', icon: 'fa-user-times' },
  MISSING_SUBSCRIBER_DOB:  { label: 'Missing subscriber DOB',  severity: 'amber', icon: 'fa-calendar-times-o' },
  INVALID_SUBSCRIBER_DOB:  { label: 'Invalid subscriber DOB',  severity: 'amber', icon: 'fa-calendar-times-o' },

  // Same amber "fix your Open Dental record" family, but the broken field is on the
  // CARRIER, not the patient: its Electronic ID matches no payer in Stedi's directory,
  // so every patient on that carrier fails. One edit fixes them all — which is why the
  // dashboard also returns `unrecognizedCarriers`, the list of records to correct.
  UNRECOGNIZED_PAYER_ID:   { label: 'Unrecognized carrier ID',  severity: 'amber', icon: 'fa-building-o' },

  MAX_REACHED:      { label: 'Maximum reached',        severity: 'amber', icon: 'fa-exclamation-triangle' },
  MAX_NEAR:         { label: 'Maximum nearly reached', severity: 'amber', icon: 'fa-exclamation-triangle' },
  WAITING_PERIOD:   { label: 'Waiting period',         severity: 'amber', icon: 'fa-clock-o' },
};

/**
 * Every code that can arrive as a row's `insurance.dataIssue` (and in the aggregate
 * `dataIssues` / `chipCounts`). TWO backend sources, deliberately listed together
 * because the UI treats them identically — "fix a record in Open Dental":
 *
 *   1. The five `BLOCK_CODES` from lib/preflight.js — the zero-cost screen that
 *      refuses to SPEND on certainly-bogus patient data. Keep EXACTLY in sync with
 *      preflight.js's BLOCK_CODES.
 *   2. `UNRECOGNIZED_PAYER_ID` from lib/dashboard-assemble.js — the CARRIER's
 *      Electronic ID matches no payer in Stedi's directory.
 *
 * ⚠ They differ in one way that matters if you ever write copy about cost: the
 * preflight five are checks we DON'T pay for because we refuse them; an unrecognized
 * carrier id costs nothing because such a check never reaches a payer (Stedi bills
 * only on reaching the payer) — the backend still attempts it. So never tell the user
 * the carrier-id one "saved" them anything; the value is knowing what to fix.
 */
export const DATA_ISSUE_CODES = [
  'MISSING_MEMBER_ID',
  'PLACEHOLDER_MEMBER_ID',
  'MISSING_SUBSCRIBER_NAME',
  'MISSING_SUBSCRIBER_DOB',
  'INVALID_SUBSCRIBER_DOB',
  'UNRECOGNIZED_PAYER_ID',
];

export function isDataIssueCode(code) {
  return DATA_ISSUE_CODES.indexOf(code) !== -1;
}

// Order the Action-Required chips render in. Any code not listed sorts to the end.
//   1. RED (status-critical) — the payer answered and the answer is bad.
//   2. AMBER, OD data issues — placed ABOVE the coverage alerts because they are
//      the only chips whose fix UNBLOCKS verification: until the Open Dental
//      record is corrected this patient never verifies at all (the nightly sweep
//      skips them too), so they're the most actionable thing on the panel.
//   3. AMBER, coverage alerts — facts from a check that DID complete. Useful to
//      know, but nothing is broken and there's no data-entry task attached.
export const CHIP_ORDER = [
  'INACTIVE',
  'UNABLE_TO_VERIFY',
  ...DATA_ISSUE_CODES,
  'MAX_REACHED',
  'MAX_NEAR',
  'WAITING_PERIOD',
];

export function chipForCode(code) {
  return CHIP_MAP[code] || { label: titleCase(code), severity: 'amber', icon: 'fa-exclamation-triangle' };
}

export function chipColors(severity) {
  if (severity === 'red') {
    return { color: COLORS.red500, bg: COLORS.red50, border: '#f8d4d5' };
  }
  return { color: COLORS.amberText, bg: COLORS.amber50, border: '#fde9c8' };
}

/**
 * Does a row match an Action-Required issue chip? THREE sources, in order:
 *   1. status-derived codes  → `verification.status`
 *   2. OD data-issue codes   → `insurance.dataIssue.code`  (lib/preflight.js)
 *   3. warning codes         → `verification.warnings[].code`
 *
 * Source 2 must be checked BEFORE the warnings scan, and reads off `insurance`,
 * not `verification`: a blocked row usually has no verification at all (the
 * check was refused before it ran), so it could never match via warnings.
 */
export function rowMatchesIssue(row, code) {
  if (!code || !row) return true;
  const status = (row.verification && row.verification.status) || null;
  if (code === 'INACTIVE') return status === 'inactive';
  if (code === 'UNABLE_TO_VERIFY') return status === 'unable_to_verify';
  if (isDataIssueCode(code)) {
    const issue = (row.insurance && row.insurance.dataIssue) || null;
    return !!(issue && issue.code === code);
  }
  const warnings = (row.verification && row.verification.warnings) || [];
  return Array.isArray(warnings) && warnings.some((w) => w && w.code === code);
}

/** The `{ code, detail }` OD data issue on a row, or null. Null-safe. */
export function rowDataIssue(row) {
  const issue = row && row.insurance && row.insurance.dataIssue;
  return issue && issue.code ? issue : null;
}

// ── Timeframe presets → date range ──────────────────────────────────────────
export const TIMEFRAMES = [
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'thisWeek', label: 'This week' },
  { key: 'nextWeek', label: 'Next week' },
  { key: 'next30', label: 'Next 30 days' },
];

export const TIMEFRAME_LABELS = TIMEFRAMES.reduce((acc, t) => {
  acc[t.key] = t.label;
  return acc;
}, {});

function nowInTz(tz) {
  return tz ? moment().tz(tz) : moment();
}

/**
 * Resolve a timeframe preset to a { from, to } window, in the SITE timezone so
 * "today" means the practice's today, not the browser's. Returns MySQL
 * datetime strings for the server plus the plain YMD bounds for the date
 * inputs.
 */
export function rangeForTimeframe(key, tz) {
  const start = nowInTz(tz).startOf('day');
  let from = start.clone();
  let end;
  switch (key) {
    case 'today':
      end = from.clone().endOf('day');
      break;
    case 'tomorrow':
      from = start.clone().add(1, 'day');
      end = from.clone().endOf('day');
      break;
    case 'thisWeek':
      end = from.clone().add(6, 'days').endOf('day');
      break;
    case 'nextWeek':
      from = start.clone().add(7, 'days');
      end = from.clone().add(6, 'days').endOf('day');
      break;
    case 'next30':
    default:
      end = from.clone().add(29, 'days').endOf('day');
      break;
  }
  return {
    from: from.format('YYYY-MM-DD HH:mm:ss'),
    to: end.format('YYYY-MM-DD HH:mm:ss'),
    fromDate: from.format('YYYY-MM-DD'),
    toDate: end.format('YYYY-MM-DD'),
  };
}

/** Wrap plain YMD date-input values into inclusive MySQL datetime bounds. */
export function ymdRangeToDateTime(fromYmd, toYmd) {
  return {
    from: fromYmd ? `${fromYmd} 00:00:00` : null,
    to: toYmd ? `${toYmd} 23:59:59` : null,
  };
}

// ── Date / time display helpers ─────────────────────────────────────────────

/**
 * Appointment time, displayed as WALL-CLOCK (no timezone conversion). Open
 * Dental stores `AptDateTime` in the practice's local time already, so we
 * format the same clock components rather than re-zoning them.
 */
export function apptTime(aptDateTime) {
  if (!aptDateTime) return '';
  const m = moment(String(aptDateTime).replace('T', ' '), 'YYYY-MM-DD HH:mm:ss');
  return m.isValid() ? m.format('h:mm A') : '';
}

/** "Today" / "Tomorrow" / "Wed, Jul 15" relative to the site's today. */
export function apptRelativeLabel(aptDateTime, tz) {
  if (!aptDateTime) return '';
  const m = moment(String(aptDateTime).replace('T', ' '), 'YYYY-MM-DD HH:mm:ss');
  if (!m.isValid()) return '';
  const today = nowInTz(tz).startOf('day');
  const target = m.clone().startOf('day');
  const diff = target.diff(today, 'days');
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return m.format('ddd, MMM D');
}

/**
 * Site-timezone exact datetime for our own timestamps (verification.checkedAt).
 * Mirrors mixin.js `formatDateTimeWitTz`: MySQL datetime strings are stored
 * without a zone but the DB runs in UTC, so parse as UTC then convert to the
 * site timezone. Used for the "Last verified" tooltip.
 */
export function formatCheckedAt(value, tz) {
  if (!value || value === '') return '';
  let temp;
  const isMySQL = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}/.test(value);
  if (tz) {
    if (isMySQL) {
      temp = moment.utc(String(value).replace('T', ' ').split('.')[0]).tz(tz);
    } else {
      temp = moment.utc(value).tz(tz);
    }
  } else {
    temp = moment(value);
  }
  return temp && temp.isValid() ? temp.format('MM/DD/YYYY h:mm a') : '';
}

/** Relative "N days ago" style label for the Last-verified column. */
export function relativeFromNow(value, tz) {
  if (!value || value === '') return '';
  const isMySQL = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}/.test(value);
  const m = tz && isMySQL
    ? moment.utc(String(value).replace('T', ' ').split('.')[0]).tz(tz)
    : moment(value);
  return m.isValid() ? m.fromNow() : '';
}

// ── Avatars ─────────────────────────────────────────────────────────────────
const CARRIER_PALETTE = [
  '#0f7259', '#7c3aed', '#2563eb', '#d97706', '#4f46e5',
  '#0e7490', '#1d4ed8', '#0f766e', '#65a30d', '#be123c',
];

function hashString(str) {
  let h = 0;
  const s = String(str || '');
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h;
}

export function carrierColor(name) {
  if (!name) return COLORS.neutral400;
  return CARRIER_PALETTE[hashString(name) % CARRIER_PALETTE.length];
}

export function carrierInitials(name) {
  if (!name) return '—';
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  const init = ((words[0] && words[0][0]) || '') + ((words[1] && words[1][0]) || '');
  return (init || String(name)[0] || '?').toUpperCase();
}

export function patientInitials(fName, lName) {
  const a = (fName && String(fName)[0]) || '';
  const b = (lName && String(lName)[0]) || '';
  const init = (a + b).toUpperCase();
  return init || '?';
}

export function patientName(patient) {
  if (!patient) return 'Unknown patient';
  const f = patient.fName || '';
  const l = patient.lName || '';
  return `${f} ${l}`.trim() || 'Unknown patient';
}

export function titleCase(str) {
  return String(str || '')
    .replace(/_/g, ' ')
    .replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());
}

// ── Detail-drawer helpers (Slice 6b) ─────────────────────────────────────────

/**
 * Money for the drawer: `$1,610` for whole dollars, `$529.60` when there are
 * cents (matches the tx-presenter exact-cents rule). null/undefined/'' / NaN →
 * an em-dash so a degraded benefit never renders "$NaN".
 */
export function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const hasCents = Math.round(Math.abs(n) * 100) % 100 !== 0;
  const opts = hasCents
    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    : { maximumFractionDigits: 0 };
  return `$${n.toLocaleString('en-US', opts)}`;
}

/** Present a bare status string (chip on PatientProfile + drawer header). */
export function statusPresentation(status, opts) {
  const o = opts || {};
  if (!status) {
    return o.hasInsurance === false ? STATUS_MAP.no_insurance : STATUS_MAP.none;
  }
  return STATUS_MAP[status] || {
    label: titleCase(status), icon: 'fa-question-circle', color: COLORS.neutral500, bg: COLORS.neutral100,
  };
}

/** Network code → human label ('in'/'out'/'mixed'/'na' per presenter-271). */
export function networkLabel(net) {
  if (net === 'in') return 'In-network';
  if (net === 'out') return 'Out-of-network';
  if (net === 'mixed') return 'In & out-of-network';
  if (net === 'na') return 'Not applicable';
  return null;
}

// ── The unlimited-maximum sentinel, shared by EVERY surface that reads a ring ─
//
// Used by the drawer's rings + maximums table AND the dashboard's benefits column.
// It lives above both on purpose: when the threshold sat down in the dashboard
// section, the drawer could not reach it without looking like a layering violation,
// so the drawer kept its own flag-only test — and the two surfaces then disagreed
// about the same row for two days (see normalizeStoredRing below).

/**
 * ⛔ FE TWIN of presenter-271.js's UNLIMITED_AMOUNT_THRESHOLD — keep the two in step.
 *
 * A dollar figure at or above this is a payer SENTINEL meaning "no maximum", not money.
 * Payers use several variants (99999999.99, 9999999, 999999.99, 9999999999), so this is
 * a THRESHOLD and never an equality test. $1,000,000 sits far above any real dental
 * annual maximum — the largest genuinely-plausible one is a few thousand dollars — and
 * far below every sentinel seen in the wild.
 *
 * Do NOT lower this toward realistic plan values: an unusually rich plan can legitimately
 * carry a $50,000 maximum, and telling an office that a capped plan is uncapped is the
 * expensive direction of this bug — nobody finds out until a claim is denied.
 */
export const UNLIMITED_AMOUNT_THRESHOLD = 1000000;

function isUnlimitedAmount(n) {
  return typeof n === 'number' && Number.isFinite(n) && n >= UNLIMITED_AMOUNT_THRESHOLD;
}

/**
 * A ring's numeric field, or null.
 *
 * ⛔ The null check is NOT redundant with Number.isFinite: `Number(null)` is 0, and `Number('')`
 * is 0 too. Without this guard a payer that reported NO remaining renders as "$0 … left" —
 * telling the front desk the benefit is exhausted when we simply have no figure. That is the
 * most damaging thing this cell could say, and it is one coercion away at all times.
 */
function ringNum(ring, field) {
  if (!ring) return null;
  const raw = ring[field];
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * normalizeStoredRing(raw) — one benefit ring, read back out of a STORED Summary and made
 * safe to render. FE twin of `normalizeStoredRing` in the service's lib/dashboard-assemble.js;
 * the output shape deliberately matches what the presenter's makeRing() writes today, so a
 * caller cannot tell a normalized legacy row from a fresh one.
 *
 * ⛔ WHY READ-TIME NORMALIZATION EXISTS AT ALL. Verifications written before 2026-08-12
 * predate the sentinel fix: they carry `planAmount: 99999999.99` with NO `unlimited` key at
 * all (measured — prod VerificationID 2 is exactly this). `/detail` returns the STORED
 * Summary and never re-presents from Raw271, so those rows are frozen and stay wrong until
 * the patient is re-checked. Anything that trusts the flag alone reprints $99,999,999.99.
 *
 * That is not hypothetical: the dashboard's benefits column normalized on read from the day
 * it shipped (2026-08-14) while the drawer's ringView still tested `ring.unlimited === true`
 * alone, so for two days the SAME row read "No annual maximum" in the table and
 * "$99,999,999.99 of $99,999,999.99" in the drawer opened from it.
 *
 * Returns null for anything unusable; never a half-filled object. The string branch mirrors
 * the backend's (JSON_EXTRACT can hand back either) and costs nothing here.
 */
export function normalizeStoredRing(raw) {
  let ring = raw;
  if (typeof ring === 'string') {
    if (ring.trim() === '') return null;
    try {
      ring = JSON.parse(ring);
    } catch (e) {
      return null;
    }
  }
  if (!ring || typeof ring !== 'object' || Array.isArray(ring)) return null;

  const planAmount = ringNum(ring, 'planAmount');
  const remainingAmount = ringNum(ring, 'remainingAmount');
  const usedAmount = ringNum(ring, 'usedAmount');

  const unlimited = ring.unlimited === true
    || isUnlimitedAmount(planAmount)
    || isUnlimitedAmount(remainingAmount);

  const hasRemaining = unlimited ? false : (ring.hasRemaining === true && remainingAmount !== null);

  return {
    planAmount: unlimited ? null : planAmount,
    // An explicit used amount survives — it is real spend and the only figure on an
    // unlimited row worth reading. (The presenter drops only the DERIVED one; a stored
    // row cannot tell us which it was, and the backend twin keeps it for the same reason.)
    usedAmount: usedAmount,
    remainingAmount: hasRemaining ? remainingAmount : null,
    hasRemaining: hasRemaining,
    coverageLevel: typeof ring.coverageLevel === 'string' ? ring.coverageLevel : null,
    degraded: unlimited ? false : !hasRemaining,
    unlimited: unlimited,
  };
}

/**
 * normalizeMaximumRow(row) — the same correction for one row of the drawer's
 * "Maximums & deductibles" table, which is a ring PLUS a label and in/out-of-network
 * splits. Only the money fields are rewritten, so the row keeps its identity: a maximums
 * row must never disappear just because its amounts were unusable — it still has a label,
 * and a silently missing benefit line is worse than a blank one.
 *
 * The in/out sub-rings are normalized too. They carry `unlimited` independently (a split
 * can be uncapped in-network and capped out), and although the drawer does not render
 * their dollar figures today, leaving a live sentinel behind for the next surface to find
 * is exactly how this bug reached two screens.
 */
export function normalizeMaximumRow(row) {
  if (!row || typeof row !== 'object') return row;
  const apply = (obj) => {
    const ring = normalizeStoredRing(obj);
    if (!ring) return obj;
    return Object.assign({}, obj, {
      planAmount: ring.planAmount,
      usedAmount: ring.usedAmount,
      remainingAmount: ring.remainingAmount,
      hasRemaining: ring.hasRemaining,
      unlimited: ring.unlimited,
    });
  };
  const out = apply(row);
  if (out.inNetwork) out.inNetwork = apply(out.inNetwork);
  if (out.outNetwork) out.outNetwork = apply(out.outNetwork);
  return out;
}

// ── Network view (In-network / Out-of-network / Both), 2026-09-29 ─────────────
//
// Asked for by an office that is out of network with every carrier: every in-network figure
// in the drawer was noise they had to read past. The site default lives in the settings blob
// (`benefitsNetworkView`, backend twin lib/settings-defaults.js); staff can flip it per drawer.
// DISPLAY ONLY — Print/PDF keep both networks.

export const NETWORK_VIEWS = ['both', 'in', 'out'];

/** Anything that is not a known view (absent, garbage, a typo'd blob) reads as 'both' — today's drawer. */
export function resolveNetworkView(value) {
  return NETWORK_VIEWS.indexOf(value) === -1 ? 'both' : value;
}

/** Column / pill wording for one network. */
export function networkViewLabel(view) {
  if (view === 'in') return 'In-network';
  if (view === 'out') return 'Out-of-network';
  return 'Both networks';
}

/**
 * Does this stored Summary carry the per-network split at all?
 *
 * The presenter started writing `inNetwork` / `outNetwork` on categories and maximums on
 * 2026-07-20. A Summary is frozen at check time, so an older verification has neither KEY —
 * and for it "out-of-network" cannot be answered, only guessed. The drawer hides the switch
 * rather than guess. (A post-split Summary always has the keys, even when their value is null.)
 */
export function hasNetworkSplit(summary) {
  if (!summary || typeof summary !== 'object') return false;
  const carries = (rows) => Array.isArray(rows) && rows.some(
    (r) => r && typeof r === 'object' && ('inNetwork' in r || 'outNetwork' in r)
  );
  return carries(summary.categories) || carries(summary.maximums);
}

/**
 * networkSide(row, side) → the figures for ONE network of a category or maximums row, or null
 * when the payer reported nothing for that network.
 *
 * ⛔ Never borrows the other network's figure. For an out-of-network office an in-network
 * number shown as theirs is a wrong quote to a patient (Nilay, 2026-09-29: show "—").
 *
 * The one case that returns the row itself: NEITHER side is present. The presenter fills a
 * side from rows flagged Y / N (and, for categories, W "not applicable"), so a row with a
 * figure but no side at all came from a payer row carrying no network indicator — a benefit
 * that applies whichever network the office is in. That is the same reading the per-code
 * rows use (presenter-271.js procedure `side()`: W or blank feeds BOTH sides).
 */
export function networkSide(row, side) {
  if (!row || typeof row !== 'object') return null;
  const inSide = row.inNetwork || null;
  const outSide = row.outNetwork || null;
  if (!inSide && !outSide) return row;
  return (side === 'in' ? inSide : outSide) || null;
}

/**
 * withSharedRemaining(row) — a maximums row whose REMAINING the payer sent once, with no
 * network indicator, gets that remaining on each side that has a plan amount but none of its own.
 *
 * Found at Gilreath 2026-09-30 (MetLife): plan $1,500 flagged in-network, $1,500 flagged
 * out-of-network, remaining $1,210 flagged neither. Before the presenter fix (backend twin:
 * presenter-271.js buildMaximums `sideFigure`) both sides were stored with no remaining, so the
 * In and Out views showed "$1,500 / remaining unavailable" while the Both view showed $1,210.
 * Stored Summaries are frozen at check time, so every existing verification needs this on READ.
 *
 * The row's scalar remaining is in-network-preferred (Y > W > N > blank). If NEITHER side has a
 * remaining, the scalar can only have come from a W / blank row — a figure that applies to both
 * networks. If either side has one, the scalar may be that side's own figure, so nothing is
 * filled (the other side stays "remaining unavailable" — never another network's number).
 * A shared remaining above a side's plan cannot be that side's, so that side is left alone.
 */
export function withSharedRemaining(row) {
  if (!row || typeof row !== 'object') return row;
  const inSide = row.inNetwork || null;
  const outSide = row.outNetwork || null;
  if (!inSide && !outSide) return row; // networkSide already reads the row itself for both
  if (row.unlimited === true || row.hasRemaining !== true) return row;
  const rem = ringNum(row, 'remainingAmount');
  if (rem === null) return row;
  if ((inSide && inSide.hasRemaining === true) || (outSide && outSide.hasRemaining === true)) return row;

  const fill = (s) => {
    if (!s || s.unlimited === true) return s;
    const plan = ringNum(s, 'planAmount');
    if (plan === null || rem > plan) return s;
    const used = ringNum(s, 'usedAmount');
    return Object.assign({}, s, {
      remainingAmount: rem,
      hasRemaining: true,
      // Same derivation as the presenter's makeRing: an explicit used amount wins.
      usedAmount: used !== null ? used : Math.round((plan - rem) * 100) / 100,
    });
  };
  return Object.assign({}, row, { inNetwork: fill(inSide), outNetwork: fill(outSide) });
}

/** One network's figures for a maximums row (networkSide, after the shared-remaining fill). */
function maximumSide(row, side) {
  return networkSide(withSharedRemaining(row), side);
}

/**
 * The "Maximums & deductibles" rows for one view. 'both' returns the rows untouched (today's
 * table). A single view swaps in that network's money fields, or marks the row `notReported`
 * with every amount nulled — the row itself never disappears (a silently missing benefit line
 * reads as "no such limit").
 *
 * Expects rows already passed through normalizeMaximumRow, so sub-rings are sentinel-safe.
 */
export function maximumsForView(rows, view) {
  const list = Array.isArray(rows) ? rows : [];
  if (view !== 'in' && view !== 'out') return list;
  return list.map((r) => {
    const s = maximumSide(r, view);
    if (!s) {
      return Object.assign({}, r, {
        planAmount: null, usedAmount: null, remainingAmount: null,
        hasRemaining: false, unlimited: false, notReported: true,
      });
    }
    return Object.assign({}, r, {
      planAmount: s.planAmount === undefined ? null : s.planAmount,
      usedAmount: s.usedAmount === undefined ? null : s.usedAmount,
      remainingAmount: s.remainingAmount === undefined ? null : s.remainingAmount,
      hasRemaining: s.hasRemaining === true,
      unlimited: s.unlimited === true,
      notReported: false,
    });
  });
}

/*
 * Which "Maximums & deductibles" rows back each ring, in preference order.
 * ⛔ FE twin of the labels presenter-271.js buildMaximums writes. The rings themselves
 * (buildRings) are in-network-preferred only, with no split — so a single-network ring is
 * derived from the matching table row's side instead.
 */
const RING_ROW_LABELS = {
  annualMaximum: ['Individual annual maximum', 'Family annual maximum'],
  deductible: ['Individual deductible', 'Family deductible'],
  ortho: ['Orthodontic lifetime max'],
};

/**
 * The raw ring objects (the `summary.rings` shape: { annualMaximum, deductible, ortho }) for
 * one view. 'both' returns the stored rings untouched. A single view reads each ring from its
 * maximums row's side; no row, or no figure for that network, is a null ring ("Not available")
 * — never the in-network ring standing in for the out-of-network one.
 */
export function ringSourcesForView(summaryRings, maximums, view) {
  const stored = summaryRings && typeof summaryRings === 'object' ? summaryRings : {};
  if (view !== 'in' && view !== 'out') return stored;
  const rows = Array.isArray(maximums) ? maximums : [];
  const out = {};
  Object.keys(RING_ROW_LABELS).forEach((key) => {
    const row = RING_ROW_LABELS[key].map((label) => rows.find((r) => r && r.label === label)).find(Boolean);
    out[key] = row ? maximumSide(row, view) : null;
  });
  return out;
}

/**
 * A benefits-by-category row's covered % for one view: { percent, notReported }.
 * 'both' is the stored scalar (today's column).
 */
export function categoryPercentForView(cat, view) {
  const c = cat && typeof cat === 'object' ? cat : {};
  const pctOf = (o) => (o && o.coveragePercent !== null && o.coveragePercent !== undefined ? o.coveragePercent : null);
  if (view !== 'in' && view !== 'out') return { percent: pctOf(c), notReported: false };
  const s = networkSide(c, view);
  return s ? { percent: pctOf(s), notReported: false } : { percent: null, notReported: true };
}

/**
 * Do a procedure row's in and out figures say the same thing? Both-null counts as the same
 * (the Both view then shows one dash, not two). 80 and '80' are the same figure.
 */
export function samePercent(a, b) {
  const blank = (v) => v === null || v === undefined || v === '';
  if (blank(a) || blank(b)) return blank(a) && blank(b);
  return Number(a) === Number(b);
}

// ── Benefit rings (screenshot6 visual language, plain CSS/SVG) ────────────────
// r = 52, stroke-width 13 → circumference = 2πr ≈ 326.73 (design's stroke-dasharray).
export const RING_RADIUS = 52;
export const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export const RING_DEFS = [
  { key: 'annualMaximum', label: 'Annual maximum', sub: 'Remaining this year', color: COLORS.teal600 },
  { key: 'deductible', label: 'Deductible', sub: 'Left to meet', color: COLORS.blue500 },
  { key: 'ortho', label: 'Orthodontic', sub: 'Lifetime allowance', color: '#7c3aed' },
];

/**
 * ringView(ring, def) → the plain-JSON view a BenefitRing renders. Handles the
 * four cases WITHOUT ever producing NaN:
 *   - unlimited     → big = "Unlimited", small = "no annual maximum", no arc
 *   - hasRemaining  → big = remaining, small = "of {plan}", arc = remaining/plan
 *   - degraded      → big = plan,      small = "remaining unavailable", no arc
 *   - no data (null)→ big = "—",       small = "Not available", no arc
 * `ring` is a presenter Summary ring: { planAmount, usedAmount, remainingAmount,
 * hasRemaining, degraded, unlimited } or null.
 */
export function ringView(rawRing, def) {
  // Normalized on READ, never trusted as stored. `rawRing` comes straight off a stored
  // Summary via /detail, so on a pre-2026-08-12 row the `unlimited` flag below is simply
  // absent while the amounts still hold the raw sentinel — see normalizeStoredRing.
  const ring = normalizeStoredRing(rawRing);
  const base = {
    key: def.key,
    label: def.label,
    sub: def.sub,
    color: def.color,
    circumference: RING_CIRCUMFERENCE,
    dashOffset: RING_CIRCUMFERENCE, // fully empty (no colored arc) by default
    big: '—',
    small: 'Not available',
    available: false,
    degraded: false,
  };
  if (!ring || typeof ring !== 'object') return base;

  // No maximum at all. The payer reports this as a sentinel (99999999.99 and friends),
  // normalized to `unlimited` with the amounts nulled — by presenter-271.js when the row
  // is written, and by normalizeStoredRing above when an older row is read. FIRST, because
  // with planAmount nulled every branch below falls through to the "—  Not available"
  // base — which claims we could not read the plan, when in fact we read it fine and the
  // answer is "there is no cap". Before this existed the sentinel reached the screen
  // literally: a ring reading "$99,999,999.99 of $99,999,999.99", overflowing its circle
  // (caught on the Mint prod pilot, 2026-08-11).
  //
  // No arc on purpose — an arc is a fraction of a maximum, and there isn't one.
  if (ring.unlimited === true) {
    return Object.assign({}, base, {
      available: true,
      big: 'Unlimited',
      small: 'no annual maximum',
    });
  }

  // ⛔ ringNum, not Number(): `Number(null)` is 0 and `Number.isFinite(0)` is true, so
  // reading these fields directly turns "the payer told us nothing" into "$0.00" — the
  // ring then announces an exhausted benefit on a plan we simply have no figure for.
  // Same coercion the dashboard cell guards against; one helper now, so it cannot be
  // fixed on one surface and left standing on the other.
  const planNum = ringNum(ring, 'planAmount');
  const remNum = ringNum(ring, 'remainingAmount');
  const hasRemaining = ring.hasRemaining === true && remNum !== null;

  if (hasRemaining) {
    let frac = 0;
    if (planNum && planNum > 0) frac = Math.max(0, Math.min(1, remNum / planNum));
    return Object.assign({}, base, {
      available: true,
      big: formatMoney(remNum),
      small: planNum !== null ? `of ${formatMoney(planNum)}` : '',
      dashOffset: RING_CIRCUMFERENCE * (1 - frac),
    });
  }
  if (planNum !== null) {
    // Degraded: a plan amount but no TQ29/33 remaining figure from the payer.
    return Object.assign({}, base, {
      available: true,
      degraded: true,
      big: formatMoney(planNum),
      small: 'remaining unavailable',
    });
  }
  return base;
}

// ── Dashboard "Remaining benefits / deductible" column ───────────────────────

/** Nothing to say. Grey em-dash, matching formatMoney's own empty rendering. */
const EMPTY_BENEFIT_CELL = { maxLine: '—', dedLine: null, muted: true, empty: true };

// UNLIMITED_AMOUNT_THRESHOLD, isUnlimitedAmount and ringNum used to live HERE, private to
// this column. That is precisely how the drawer ended up with its own flag-only test and
// disagreed with this cell about the same row — so they now sit at the top of the file,
// above every surface that reads a ring.

/**
 * annualMaximumLine(ring) → the FIRST line of the dashboard cell.
 *
 * Deliberately shorter wording than the drawer's ring ("$1,250" over "of $2,000"): a table
 * cell is read in a glance while scanning a column, so the two facts have to survive on one
 * line. The underlying cases are the same four ringView handles, and they must stay in
 * agreement — if you change a rule here, change it there.
 */
function annualMaximumLine(ring) {
  if (!ring) return null;

  const plan = ringNum(ring, 'planAmount');
  const remaining = ringNum(ring, 'remainingAmount');

  // No cap at all. Said in words because there is no number to print — and because the
  // alternative (falling back to the out-of-network figure, which Delta does supply) would
  // state a cap that does not apply to the office actually treating the patient.
  //
  // The threshold test runs alongside the flag so a frozen pre-2026-08-12 row — which
  // carries the raw sentinel and no flag at all — lands here rather than printing it.
  if (ring.unlimited === true || isUnlimitedAmount(plan) || isUnlimitedAmount(remaining)) {
    return 'No annual maximum';
  }

  if (ring.hasRemaining === true && remaining !== null) {
    return plan !== null
      ? `${formatMoney(remaining)} of ${formatMoney(plan)} left`
      : `${formatMoney(remaining)} left`;
  }

  // ⛔ The payer gave a maximum but no remaining. State the maximum and claim NOTHING about
  // what is left — "$2,000 left" here would be a number we invented, which is the same
  // failure as printing a service date the payer never sent.
  if (plan !== null) return `${formatMoney(plan)} maximum`;

  return null;
}

/**
 * deductibleLine(ring) → the SECOND line. Three phrasings rather than one, because "$0
 * deductible left" is technically true and reads as an error; the front desk wants to know
 * whether the deductible is a thing they still have to collect.
 */
function deductibleLine(ring) {
  if (!ring) return null;

  const plan = ringNum(ring, 'planAmount');
  const remaining = ringNum(ring, 'remainingAmount');

  // "Unlimited" is meaningless for a deductible, and a sentinel-valued one is a payer
  // artifact rather than money the patient owes. Say nothing either way.
  if (ring.unlimited === true || isUnlimitedAmount(plan) || isUnlimitedAmount(remaining)) return null;

  const hasRemaining = ring.hasRemaining === true && remaining !== null;

  // The plan simply has no deductible. Measured on prod: Delta returns planAmount 0.
  if (plan === 0 && (!hasRemaining || remaining === 0)) return 'No deductible';

  if (hasRemaining) {
    if (remaining <= 0) return 'Deductible met';
    return `${formatMoney(remaining)} deductible left`;
  }

  // Amount known, usage unknown — MetLife does exactly this. No "left".
  if (plan !== null) return `${formatMoney(plan)} deductible`;

  return null;
}

/**
 * benefitCellView(benefits, opts) → { maxLine, dedLine, muted, empty }
 *
 * The dashboard cell for ONE plan. `benefits` is `row.benefits` (or
 * `row.secondary.benefits`) as assembled by lib/dashboard-assemble.js:
 * `{ annualMaximum: ring|null, deductible: ring|null }`.
 *
 * `opts.isStale` mutes the text without hiding it: a 40-day-old maximum is still the best
 * number anyone has, but it must not read like money confirmed this morning. The LAST
 * VERIFIED column carries the actual age.
 *
 * Pure and dependency-free so it can be pinned against real stored rings in a node test —
 * which matters here, because the inputs include frozen pre-2026-08-12 summaries that no
 * longer resemble what the presenter writes today.
 */
export function benefitCellView(benefits, opts) {
  const o = opts || {};
  if (!benefits || typeof benefits !== 'object') return EMPTY_BENEFIT_CELL;

  const maxLine = annualMaximumLine(benefits.annualMaximum);
  const dedLine = deductibleLine(benefits.deductible);

  if (maxLine === null && dedLine === null) return EMPTY_BENEFIT_CELL;

  return {
    maxLine: maxLine === null ? '—' : maxLine,
    dedLine: dedLine,
    muted: o.isStale === true,
    empty: false,
  };
}

// ── "Checks used this month" usage meter (shared) ────────────────────────────

/** Coerce any count-ish value to a finite number, defaulting to 0. Every count that
 *  reaches these formatters is optional and may arrive missing, null, or garbage on a
 *  partial/older backend payload — a NaN or undefined must never reach a rendered figure. */
function toCount(value) {
  const n = Number(value);
  // Floor negatives to 0. A reconciliation/backfill correction can momentarily carry a NEGATIVE
  // counter, and a band must never render '-1–5' (nor a negative bar/column). Non-finite → 0.
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

/**
 * usageMeterView(counts) → the plain-JSON view the "Checks used this month" meter renders,
 * shared by the Precheck settings modal and the Credits-tab Usage report card so the band /
 * plural / split rules live in ONE place instead of two templates.
 *
 * `counts` is the `/insurance-verification/usage` payload's `data.counts`:
 *   { totalPersistedChecks, billableChecks, unknownChecks, freeChecks, unclassifiedChecks,
 *     byTrigger {nightly,onDemand,onBooking}          — BILLABLE-only per trigger,
 *     byTriggerTotal {nightly,onDemand,onBooking}     — ALL persisted per trigger (MAY BE
 *                                                        ABSENT on an older deployed backend),
 *     reconciliation { atLeast, atMost } }
 *
 * ⛔ THE BAND IS A HARD PRODUCT CONSTRAINT — do not re-derive it. `headline` is the
 * reconciliation band `atLeast–atMost` (en-dash), collapsed to a single number ONLY when the
 * two are equal. `Billable` is three states (yes / unknown / no); flattening the band to one
 * summed figure either over-states a client's usage — the number they audit — or discards the
 * evidence that would let the uncertain class be proven free later. NEVER compute the headline
 * from billableChecks / the split / unknownChecks.
 *
 * Returns:
 *   {
 *     headline: '12' | '3–5',            // reconciliation verbatim (en-dash U+2013 band)
 *     isRange: boolean,                  // atLeast !== atMost
 *     unit: 'billed check' | 'billed checks',   // singular ONLY when atMost === 1
 *     split: [                           // nightly/onDemand always; onBooking conditionally
 *       { key:'nightly',  label:'Automatic',   billable, total },
 *       { key:'onDemand', label:'Manual',      billable, total },
 *       { key:'onBooking',label:'On booking',  billable, total },  // omitted when billable===0
 *                                                                  //   AND (total null or 0)
 *     ],
 *     free: number,                      // freeChecks passthrough
 *     totalPersistedChecks: number,      // passthrough
 *     unknownNote: string | null,        // null when unknownChecks===0, else the meter sentence
 *   }
 *
 * `total` is null for every split entry when `byTriggerTotal` is absent (older backend).
 * Defensive throughout: a missing / partial / garbage `counts` yields safe zeros and null,
 * never a throw and never a NaN on screen.
 */
export function usageMeterView(counts) {
  const c = (counts && typeof counts === 'object') ? counts : {};

  // Band, read straight off reconciliation — never re-derived (see the ⛔ note above).
  const recon = (c.reconciliation && typeof c.reconciliation === 'object') ? c.reconciliation : {};
  const atLeast = toCount(recon.atLeast);
  const atMost = toCount(recon.atMost);
  const isRange = atLeast !== atMost;
  const headline = isRange ? `${atLeast}–${atMost}` : `${atLeast}`;
  // Pluralize on the CEILING, matching the modal's `v-if="atMost !== 1"`: singular only
  // when the most we could have billed is exactly one check.
  const unit = atMost === 1 ? 'billed check' : 'billed checks';

  const byTrigger = (c.byTrigger && typeof c.byTrigger === 'object') ? c.byTrigger : {};
  // byTriggerTotal MAY BE ABSENT on an older deployed backend — absent means "totals unknown",
  // which we represent as null per entry (NOT 0, which would claim zero persisted checks).
  const byTriggerTotal = (c.byTriggerTotal && typeof c.byTriggerTotal === 'object') ? c.byTriggerTotal : null;

  const SPLIT_DEFS = [
    { key: 'nightly', label: 'Automatic' },
    { key: 'onDemand', label: 'Manual' },
    { key: 'onBooking', label: 'On booking' },
  ];
  const split = [];
  SPLIT_DEFS.forEach((def) => {
    const billable = toCount(byTrigger[def.key]);
    const total = byTriggerTotal ? toCount(byTriggerTotal[def.key]) : null;
    // onBooking is the only optional segment: it disappears when there is nothing to say —
    // no billable checks AND no (or a zero) persisted total. Automatic/Manual always show.
    if (def.key === 'onBooking' && billable === 0 && (total === null || total === 0)) return;
    split.push({
      key: def.key, label: def.label, billable, total,
    });
  });

  const free = toCount(c.freeChecks);
  const totalPersistedChecks = toCount(c.totalPersistedChecks);
  const unknown = toCount(c.unknownChecks);
  // The exact sentence the modal renders — pinned by
  // cypress/node-tests/insurance-verification-usage-meter.test.js. Any wording change breaks it.
  const unknownNote = unknown === 0
    ? null
    : `${unknown} check${unknown !== 1 ? 's' : ''} could not be confirmed as billed either way, which is why this is a range.`;

  return {
    headline, isRange, unit, split, free, totalPersistedChecks, unknownNote,
  };
}

// ── Usage report-card / IV-section visibility gating ─────────────────────────

/**
 * ivUsageSectionVisible({ flagEnabled, totalPersistedChecks }) → should the Credits-tab
 * Insurance Verification usage SECTION render?
 *
 * True when the site flag is ON (the practice is enabled) OR the site has ANY persisted checks.
 * The second term is deliberate: a site can be turned OFF after it has already spent, and its
 * billing history must survive that so a client can still reconcile a past invoice.
 *
 * `flagEnabled` is the resolved IV flag boolean — computed UPSTREAM by the caller with the same
 * predicate the sidebar / router use (`settings.INSURANCE_VERIFICATION_ENABLED.PropertyValue
 * === 'TRUE'`, mirrored by mixin `insuranceVerificationEnabled`). It is compared strictly to
 * `true` so a stray truthy non-boolean can never masquerade as "enabled".
 */
export function ivUsageSectionVisible({ flagEnabled, totalPersistedChecks } = {}) {
  return flagEnabled === true || toCount(totalPersistedChecks) > 0;
}

/**
 * usageCardVisible({ smsFeatureEnabled, ivVisible }) → should the WHOLE Usage report card
 * render? The card hosts both the SMS/email meter and the IV section, so it shows when EITHER
 * has something to show, and disappears only when neither does. Pure OR, coerced to a boolean
 * so undefined inputs resolve to `false` rather than a crash.
 */
export function usageCardVisible({ smsFeatureEnabled, ivVisible } = {}) {
  return smsFeatureEnabled === true || ivVisible === true;
}

// ── IV usage view: drill-down row labels + bar-width math (Credits-tab section, SuperAdmin) ────
//
// The Credits-tab Insurance Verification section renders a bar per trigger plus a drill-down log of
// individual checks, and the SuperAdmin "Insurance Checks" rollup renders a per-site table. These
// pure maps keep both templates thin and are pinned by
// cypress/node-tests/insurance-verification-usage-view.test.js so the visible strings can't drift.

/**
 * A row's `trigger` → the human label the log / Trigger column shows. Backend trigger strings are
 * 'nightly' / 'on_demand' / 'on_booking' (Automatic / Manual / On booking). Unknown → titleCased;
 * absent → ''.
 */
export function ivTriggerLabel(trigger) {
  if (trigger === 'nightly') return 'Automatic';
  if (trigger === 'on_demand') return 'Manual';
  if (trigger === 'on_booking') return 'On booking';
  return trigger ? titleCase(trigger) : '';
}

/**
 * A check row's `status` → the Result column label. Only 'verified' and 'inactive' get their own
 * word; everything else (error / unable_to_verify / pending / null) reads 'Failed', per the product
 * spec for this compact report column.
 */
export function ivCheckResultLabel(status) {
  if (status === 'verified') return 'Verified';
  if (status === 'inactive') return 'Inactive';
  return 'Failed';
}

/**
 * A check row's `billable` tri-state → the Billed column label. 'yes'→Billed, 'unknown'→Unconfirmed,
 * 'no'→Free; null / undefined / anything else → em-dash (we never invent a billing state).
 */
export function ivCheckBilledLabel(billable) {
  if (billable === 'yes') return 'Billed';
  if (billable === 'unknown') return 'Unconfirmed';
  if (billable === 'no') return 'Free';
  return '—';
}

/**
 * A backend usage-log row → the rendered drill-down cells. This is the ONE place that names the
 * row's field KEYS (`triggerType` / `status` / `billable` / `patientName` / `patNum` — the verbatim
 * usage-log-handler contract), so the template stays thin over it AND a field-name drift (e.g.
 * reading `trigger` instead of `triggerType`, which renders a blank column) fails the node-test
 * instead of shipping silently. The DATE is intentionally not here: it needs the site timezone,
 * which is a component concern. Null-safe — a garbage row yields safe strings, never a throw.
 */
export function ivCheckLogCells(row) {
  const r = (row && typeof row === 'object') ? row : {};
  return {
    patient: r.patientName || `PatNum ${r.patNum}`,
    trigger: ivTriggerLabel(r.triggerType),
    result: ivCheckResultLabel(r.status),
    billed: ivCheckBilledLabel(r.billable),
  };
}

/**
 * The number a split entry contributes to its bar (both the printed figure AND the bar width):
 * `total ?? billable`. `total` is the all-persisted count and WINS whenever present; it is null only
 * on an older backend that omits byTriggerTotal, in which case we fall back to the billable-only
 * count. ⚠ Never collapse this to `billable`: when a total exists the bar must show ALL persisted
 * checks for that trigger, not just the billable subset. Coerced to a safe number.
 */
export function ivSplitEntryCount(entry) {
  if (!entry || typeof entry !== 'object') return 0;
  return entry.total == null ? toCount(entry.billable) : toCount(entry.total);
}

/** The largest bar value across a split (each bar width is relative to it). 0 for an empty split. */
export function ivSplitBarMax(split) {
  if (!Array.isArray(split)) return 0;
  return split.reduce((m, e) => Math.max(m, ivSplitEntryCount(e)), 0);
}

/**
 * Bar width for a usage-section entry, relative to the max across the entries. Mirrors the SMS
 * card's usageBarWidth: a floor of 2% so a non-zero bar is always visible, 0% only for a true zero
 * (or a non-positive / garbage max). Returns a CSS width string.
 */
export function ivUsageBarWidth(value, max) {
  const v = Number(value);
  const m = Number(max);
  if (!Number.isFinite(v) || v <= 0 || !Number.isFinite(m) || m <= 0) return '0%';
  return `${Math.max(2, Math.round((v / m) * 100))}%`;
}

/**
 * SuperAdmin per-site rollup: the "all persisted per trigger" count for one trigger key
 * ('nightly' / 'onDemand' / 'onBooking'), preferring `byTriggerTotal` and falling back to
 * `byTrigger` (the billable-only count) when an older backend omits the totals. Garbage / missing
 * → 0. Only healthy rows call this; a failed site (counts === null) is rendered separately.
 */
export function ivRollupTriggerCount(counts, key) {
  if (!counts || typeof counts !== 'object') return 0;
  const total = (counts.byTriggerTotal && typeof counts.byTriggerTotal === 'object')
    ? counts.byTriggerTotal[key]
    : undefined;
  if (total !== undefined && total !== null && Number.isFinite(Number(total))) return toCount(total);
  const bill = (counts.byTrigger && typeof counts.byTrigger === 'object') ? counts.byTrigger[key] : undefined;
  return toCount(bill);
}

/**
 * SuperAdmin per-site rollup: the merged "Unknown" column = unconfirmed-billable (`unknownChecks`)
 * plus unclassified (`unclassifiedChecks`). One number, with both named in a tooltip upstream.
 */
export function ivRollupUnknownCount(counts) {
  if (!counts || typeof counts !== 'object') return 0;
  return toCount(counts.unknownChecks) + toCount(counts.unclassifiedChecks);
}

/**
 * Is a SuperAdmin usage-rollup `sendAction` response a genuine SUCCESS payload?
 *
 * ⚠ `superadmin/sendAction` NEVER throws — on a backend failure it RESOLVES `{ status, data }` with
 * `data` a BARE STRING (createErrorResponse serialises `JSON.stringify(message)`). The naive
 * `if (response && response.data)` guard let that error string land in the tab's data prop, and the
 * template's `.sites.length` read then threw a TypeError DURING RENDER — the 2026-08-15
 * SEED_TX_PRESENTER_DEFAULTS incident class (fix a9f7884e). A real rollup is a 2xx whose body is an
 * OBJECT carrying an array `sites` (both the IV and SMS rollups share that shape), so require exactly
 * that; anything else (error string, non-2xx, missing sites, a bare array) is invalid → show the
 * server's real reason and render nothing.
 */
export function isValidRollupPayload(response) {
  const status = response && response.status;
  const data = response && response.data;
  return typeof status === 'number' && status >= 200 && status < 300
    && !!data && typeof data === 'object' && !Array.isArray(data)
    && Array.isArray(data.sites);
}

/**
 * The plan-facts grid (drawer "Plan summary"). Always emits the 8 core facts
 * (design), then any of the optional three that are present. `verification` is
 * the raw row (fallbacks for carrier/member/payer when the Summary lacks them).
 */
export function buildPlanFacts(summary, verification) {
  const plan = (summary && summary.plan) || {};
  const v = verification || {};
  const status = (summary && summary.status) || v.Status || null;
  const statusLabel = status
    ? (STATUS_MAP[status] ? STATUS_MAP[status].label : titleCase(status))
    : null;

  const val = (x) => (x === null || x === undefined || x === '' ? '—' : String(x));
  const facts = [
    { k: 'Status', v: val(statusLabel) },
    { k: 'Carrier', v: val(plan.carrierName || v.CarrierName) },
    { k: 'Plan name', v: val(plan.planName) },
    { k: 'Plan type', v: val(plan.planType) },
    { k: 'Group #', v: val(plan.groupNumber) },
    { k: 'Member ID', v: val(plan.memberId || v.MemberID) },
    { k: 'Payer ID', v: val(plan.payerId || v.PayerID) },
    { k: 'Network', v: val(networkLabel(plan.network)) },
  ];
  if (plan.effectiveDate) facts.push({ k: 'Effective date', v: String(plan.effectiveDate) });
  if (plan.fiscalYear) facts.push({ k: 'Benefit year', v: String(plan.fiscalYear) });
  if (plan.cob === true) facts.push({ k: 'Coordination of benefits', v: 'Yes' });
  return facts;
}

/** Format an OD procedure date ('YYYY-MM-DD'[ time]) as wall-clock MM/DD/YYYY. */
export function formatProcDate(value) {
  if (!value) return '';
  const m = moment(String(value).replace('T', ' '), ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD']);
  return m.isValid() ? m.format('MM/DD/YYYY') : '';
}

/**
 * A presenter `frequency` object → a compact human string for the procedure
 * table, e.g. { quantity: 2, per: 'calendar year' } → "2 / calendar year".
 *
 * A frequency needs BOTH a count and a period. A bare count ("2 visits", "1 units")
 * or a bare period ("calendar year" alone) is NOT a frequency and yields '' (the cell
 * shows an em-dash). The `quantityQualifier` (e.g. the X12 "FL/units" that produced the
 * awkward "1 units") is intentionally NOT shown — the row's procedure description already
 * names the service, so "2 / calendar year" next to "Cleaning (adult)" reads clearly.
 * Kept in lockstep with the PDF's ProcedureTable.formatFrequency. Null / non-object /
 * all-empty → '' (never "undefined"/"NaN"). `quantity` 0 is a real count ("0 / calendar year").
 */
export function formatFrequency(freq) {
  if (!freq || typeof freq !== 'object') return '';
  const q = freq.quantity;
  const hasQ = q !== null && q !== undefined && q !== '' && Number.isFinite(Number(q));
  const per = (typeof freq.per === 'string' && freq.per.trim() !== '') ? freq.per.trim() : '';
  if (!hasQ || !per) return '';
  return `${Number(q)} / ${per}`;
}

/**
 * A presenter `remaining` object → how many uses are LEFT, e.g. "1 of 2 left".
 *
 * The payer states this outright ("1 Unit / Remaining"); it is the most
 * denial-preventing fact in the whole response, and until 2026-07-21 the presenter
 * discarded it. The allowance is passed in only to supply the denominator.
 *
 * ⚠ ZERO IS A REAL AND IMPORTANT VALUE. Any falsy shortcut (`if (!q) return ''`)
 * hides exactly the case that stops a practice booking an appointment the payer
 * will deny. `remainingSeverity` below turns that case red.
 * ⚠ NEVER invent a denominator: with no allowance this reads "1 left", not "1 of ? left".
 * ⚠ TWINNED with the PDF's ProcedureTable.formatRemaining — the drawer and the handout
 * must never disagree about a number the patient is quoted.
 *
 * `remaining.per` (the X12 qualifier, literally "remaining") is deliberately not shown.
 */
export function formatRemaining(remaining, allowance) {
  if (!remaining || typeof remaining !== 'object') return '';
  const q = remaining.quantity;
  if (q === null || q === undefined || q === '' || !Number.isFinite(Number(q))) return '';
  const left = Number(q);
  const a = allowance && typeof allowance === 'object' ? allowance.quantity : null;
  const hasA = a !== null && a !== undefined && a !== '' && Number.isFinite(Number(a));
  // Only show "of N" when N is a denominator that makes sense for this count.
  if (hasA && Number(a) >= left) return `${left} of ${Number(a)} left`;
  return `${left} left`;
}

/**
 * 'red' when the benefit is used up, 'green' when uses remain, '' when the payer
 * said nothing. Absent is NOT zero — we never infer exhaustion from silence.
 */
export function remainingSeverity(remaining) {
  if (!remaining || typeof remaining !== 'object') return '';
  const q = remaining.quantity;
  if (q === null || q === undefined || q === '' || !Number.isFinite(Number(q))) return '';
  return Number(q) <= 0 ? 'red' : 'green';
}

/**
 * A presenter per-code note → the chip label shown under a procedure row.
 *
 * ⛔ The payer's own words are NEVER rewritten. For `scope` and `verbatim` the text is
 * passed straight through; only the two recognised flags get our phrasing, and those were
 * matched by exact full-string equality upstream (lib/limitation-notes.js) precisely so
 * this label can be trusted. See that file's semantic-inversion note before adding a kind.
 */
export function noteChipLabel(note) {
  if (!note || typeof note !== 'object') return '';
  const text = typeof note.text === 'string' ? note.text : '';
  switch (note.kind) {
    case 'noDeductible':
      return 'No deductible';
    case 'alternate':
      return 'Alternate benefits may apply';
    case 'shares': {
      const codes = Array.isArray(note.codes) ? note.codes.filter(Boolean) : [];
      return codes.length ? `Shares frequency: ${codes.join(', ')}` : text;
    }
    default:
      return text; // scope + verbatim — verbatim is always safe
  }
}

/** Chip tone per note kind. Only a confirmed flag earns colour; everything else is neutral. */
export function noteChipTone(note) {
  if (!note || typeof note !== 'object') return 'neutral';
  if (note.kind === 'noDeductible') return 'good';
  if (note.kind === 'alternate') return 'warn';
  return 'neutral';
}

/**
 * An age limit → "Age 12 and up" / "Through age 25" / "Ages 12–25", or '' when it does
 * not constrain. The presenter already suppresses no-op limits (a payer sending 99 on
 * every code), so anything arriving here is real.
 */
export function formatAgeLimit(ageLimit) {
  if (!ageLimit || typeof ageLimit !== 'object') return '';
  const min = Number.isFinite(Number(ageLimit.min)) && ageLimit.min !== null ? Number(ageLimit.min) : null;
  const max = Number.isFinite(Number(ageLimit.max)) && ageLimit.max !== null ? Number(ageLimit.max) : null;
  if (min !== null && max !== null) return `Ages ${min}–${max}`;
  if (max !== null) return `Through age ${max}`;
  if (min !== null) return `Age ${min} and up`;
  return '';
}

// ── Verify Now messaging ─────────────────────────────────────────────────────

/**
 * Normalize whatever the caller has into a list of verbatim block `detail`
 * strings. Accepts the /verify payload (`{ blocks: [...] }`), a bare blocks
 * array, or a single `{ code, detail }` (a dashboard row's `insurance.dataIssue`,
 * which the page falls back to — see the note on `verifyReasonMessage`).
 *
 * `detail` is written by the backend FOR a front-desk reader and is rendered
 * VERBATIM — never paraphrase it here. A block with no `detail` falls back to
 * its chip label so we never emit an empty bullet.
 */
export function blockDetails(data) {
  let list = null;
  if (Array.isArray(data)) list = data;
  else if (data && Array.isArray(data.blocks)) list = data.blocks;
  else if (data && data.code) list = [data];
  if (!list) return [];

  const out = [];
  const seen = {};
  list.forEach((b) => {
    if (!b) return;
    const text = (typeof b.detail === 'string' && b.detail.trim() !== '')
      ? b.detail.trim()
      : (b.code ? chipForCode(b.code).label : '');
    if (!text || seen[text]) return;
    seen[text] = true;
    out.push(text);
  });
  return out;
}

// The zero-cost fact, stated in every blocked_bad_data message. A refused check
// is NOT a failed check — nothing was sent and nothing was billed — and if we
// don't say so the front desk reads "unable to verify" as money wasted.
const NO_CHARGE = 'No check was run (no charge)';

function blockedBadDataMessage(data) {
  const details = blockDetails(data);
  if (details.length === 0) {
    // The /verify handler does not currently echo `blocks` (and the caller may
    // have no row to fall back on), so stay useful without them.
    return `${NO_CHARGE} — this patient's insurance details in Open Dental are incomplete. Fix them in Open Dental, then verify again.`;
  }
  if (details.length === 1) {
    return `${NO_CHARGE} — fix this in Open Dental: ${details[0]}`;
  }
  return `${NO_CHARGE} — fix these in Open Dental: ${details.join('; ')}`;
}

/**
 * Copy for the STEP 2.5 payer-capability bailout. This one is NOT a to-do item and
 * NOT a data-entry mistake: the payer simply does not do electronic eligibility, so
 * there is nothing to fix in Open Dental and retrying will never help. The message
 * therefore points at the only path that works — phone the payer — instead of
 * implying the office did something wrong.
 *
 * `data` optionally carries `payerName` / `payerId` from /verify, so we can name the
 * payer the office has to call. Falls back to generic wording when absent.
 */
/**
 * excluded_carrier — the office itself put this carrier on the exclude list, because its
 * 271 carries no usable benefits (network/capitation products like Cigna PPO Access Plus).
 *
 * ⚠ Deliberately names the carrier AND says where to undo it. This refusal is the one the
 * office caused, so "why won't this verify?" has to be answerable from the message alone —
 * otherwise it becomes a support call about a setting they chose.
 *
 * ⚠ It is TERMINAL (see TERMINAL_VERIFY_REASONS) even though it is reversible in seconds,
 * which diverges from `unmapped_carrier`. Both are office-fixable, but offering "Verify now"
 * directly under "this carrier is excluded" is exactly the confusion that list exists to
 * remove. Terminal WHILE excluded, not terminal forever.
 */
function excludedCarrierMessage(data) {
  const d = data || {};
  const name = typeof d.carrierName === 'string' && d.carrierName.trim() !== ''
    ? d.carrierName.trim()
    : null;
  const who = name ? `${name} is` : 'This carrier is';
  return `${NO_CHARGE} — ${who} excluded from electronic verification. `
    + 'Verify this plan by phone, or remove the exclusion in Precheck settings → Carriers.';
}

function payerNoEligibilityMessage(data) {
  const d = data || {};
  const name = typeof d.payerName === 'string' && d.payerName.trim() !== ''
    ? d.payerName.trim()
    : null;
  const who = name ? `${name} doesn't` : "This patient's payer doesn't";
  return `${NO_CHARGE} — ${who} support electronic verification. `
    + 'Verify this plan by phone with the payer.';
}

/**
 * Reasons that are TERMINAL: nothing the office can do makes this check succeed.
 *
 * Today that is exactly `payer_no_eligibility` — Stedi's own directory says the payer
 * does not do 270/271 at all, so there is no Open Dental field to correct and no retry
 * that helps. Every OTHER bailout reason is fix-and-retry:
 *   no_billing_provider  -> set it in Precheck settings, then verify
 *   no_billing_npi       -> add the billing provider's NPI in Open Dental, then verify
 *   unmapped_carrier     -> add the mapping, then verify
 *   blocked_bad_data     -> fix the member id / DOB in OD, then verify
 *   no_active_insurance  -> add the plan in OD, then verify
 *
 * WHY THIS EXISTS (Mint prod pilot, 2026-08-11): after a terminal refusal the drawer
 * showed the correct amber "no charge — verify by phone" banner and, directly beneath
 * it, the "Not verified yet — this patient hasn't been checked yet" empty state with a
 * green **Verify now** button. Two panels arguing, and the prominent one invited a click
 * that can only ever refuse again. The distinction was already described in
 * payerNoEligibilityMessage's comment ("retrying will never help") but nothing in the
 * code could act on it, so the UI could not tell the two apart.
 *
 * ⚠ Adding a reason here REMOVES the retry affordance for it. Only add one when a retry
 * is genuinely incapable of succeeding — a merely *likely* failure still deserves the
 * button, because the row snapshot can be minutes stale and the office may have just
 * fixed the record.
 */
export const TERMINAL_VERIFY_REASONS = ['payer_no_eligibility', 'excluded_carrier'];

export function isTerminalVerifyReason(reason) {
  return typeof reason === 'string' && TERMINAL_VERIFY_REASONS.indexOf(reason) !== -1;
}

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 * "This failed, and the system is already going to try again" — the retry notice.
 * ─────────────────────────────────────────────────────────────────────────────────────
 *
 * WHY IT EXISTS. Mint's first nightly run (2026-08-19) hit a MetLife outage. Two rows read
 * "Unable to verify" with no hint that the runner would retry them free that night — so the
 * only rational thing a front desk can do is click Verify, paying $0.30 for a check that was
 * already coming. An absent explanation is not neutral here; it actively costs money.
 *
 * ⛔ THIS IS THE OTHER TAXONOMY. `TERMINAL_VERIFY_REASONS` above is about a /verify
 * PRE-FLIGHT bailout (`reason` — why we refused to spend). This is about a PERSISTED row's
 * `errorType` (why a check that ran did not succeed). They share no values and must not be
 * conflated.
 */
export const TRANSIENT_ERROR_TYPES = ['payer_unavailable', 'transport'];

/**
 * isTransientFailure(verification) — would the nightly runner treat this row as retryable?
 *
 * ⛔ MIRRORS lib/precheck-run.js classifyHistoryRow, INCLUDING ITS FIRST BRANCH, and the
 * second half is the one that is easy to miss:
 *
 *   1. status === 'error'  → transient, whatever the errorType — EXCEPT 'invalid_request'.
 *      classifyOutcome only produces 'error' for HTTP-level failures (network, 429) that never
 *      reached the payer. The exception (2026-09-18, runner change in the same release): Stedi
 *      rejecting OUR request (HTTP 400, e.g. "Invalid Participant Identification") is the same
 *      request tomorrow, so the runner now BACKS IT OFF instead of retrying — promising "will
 *      retry tonight" for it would be a promise the runner does not keep.
 *   2. status === 'unable_to_verify' AND errorType is transient → transient. This is the 2xx
 *      case where the payer answered with an unavailability AAA code.
 *
 * Checking only (2) — the obvious reading — would silently miss every network failure and
 * every rate-limit, which are exactly the outages this notice exists for.
 */
export function isTransientFailure(verification) {
  const v = verification && typeof verification === 'object' ? verification : null;
  if (!v) return false;
  const status = typeof v.status === 'string' ? v.status : null;
  if (status === 'error') return v.errorType !== 'invalid_request';
  if (status !== 'unable_to_verify') return false;
  const et = typeof v.errorType === 'string' ? v.errorType : null;
  return et !== null && TRANSIENT_ERROR_TYPES.indexOf(et) !== -1;
}

// Epoch ms for one of our timestamps (MySQL UTC string) or an OD date, else null.
function utcMs(value) {
  if (!value || value === '') return null;
  const m = moment.utc(String(value).replace(' ', 'T').split('.')[0]);
  return m.isValid() ? m.valueOf() : null;
}

/**
 * lastCheckNote({ verification, lastVerified, lastChecked, carrierName, tz }) → string | null
 *
 * The subline under "Last verified" when our latest check did NOT verify:
 * "Checked 3 hours ago · couldn't reach Delta Dental Of Tennessee".
 *
 * WHY. lastVerified counts SUCCESSFUL checks only (plus Open Dental's date) since 2026-09-23 —
 * a transport error on dev PatNum 2655 used to read "Last verified: a few seconds ago". With
 * the honest value a failed check would be invisible under that label, so this says what
 * happened to it. Null when the latest check verified, there is none, or a later verification
 * (e.g. Open Dental's date) already supersedes it.
 *
 * `lastChecked` is additive on the backend; an older one omits it, so this falls back to
 * `verification.checkedAt` (the same value).
 */
export function lastCheckNote({ verification, lastVerified, lastChecked, carrierName, tz } = {}) {
  const v = verification && typeof verification === 'object' ? verification : null;
  const status = v && typeof v.status === 'string' ? v.status : null;
  if (!status || status === 'verified' || status === 'pending') return null;

  const checked = lastChecked || v.checkedAt || null;
  const checkedMs = utcMs(checked);
  const verifiedMs = utcMs(lastVerified);
  if (checkedMs !== null && verifiedMs !== null && verifiedMs >= checkedMs) return null;

  const et = typeof v.errorType === 'string' ? v.errorType : null;
  let outcome = 'not verified';
  if (isTransientFailure(v)) outcome = `couldn't reach ${carrierName || 'the payer'}`;
  else if (status === 'inactive') outcome = 'payer reports coverage inactive';
  else if (et === 'member_not_found') outcome = 'patient not found at payer';
  else if (et === 'provider_config') outcome = "payer didn't recognize the billing provider";
  else if (et === 'invalid_request') outcome = 'payer rejected the request';

  const when = checked ? relativeFromNow(checked, tz) : '';
  return when ? `Checked ${when} · ${outcome}` : `Last check: ${outcome}`;
}

/**
 * retryNoticeFor(row, automation, now) → { text } | null
 *
 * ⛔ SAYS NOTHING UNLESS IT IS CERTAIN. Three independent silences, each deliberate:
 *   • not a transient failure  → a human must act; a retry promise would be a lie
 *   • automation off           → nothing is coming, so promising it is a lie
 *   • no appointment date      → we cannot tell tonight from three weeks away
 *
 * ⛔ AND IT NEVER SAYS "TONIGHT" FOR A ROW OUTSIDE THE LOOKAHEAD. The dashboard's own range
 * is routinely far wider than `lookaheadDays` (the office picks "Next 30 days"; the runner
 * scans 5), so a naive "will retry tonight" would be wrong for MOST failed rows on screen —
 * the common case, not an edge case. The runner's window is
 * [now, endOf(today + lookaheadDays)] (precheck-run.js), mirrored here.
 *
 * This states INTENT from facts that are true at render time. It deliberately does not try to
 * predict the runner's actual decision — that is made at 04:00 against state we do not have
 * (the back-off history above all), and a prediction that drifts is worse than silence.
 */
export function retryNoticeFor(row, automation, now) {
  const r = row && typeof row === 'object' ? row : null;
  const a = automation && typeof automation === 'object' ? automation : null;
  if (!r || !a || a.autoPrecheckEnabled !== true) return null;
  if (!isTransientFailure(r.verification)) return null;

  const carrier = (r.insurance && r.insurance.carrierName) || null;
  const lead = carrier ? `Couldn't reach ${carrier}` : "Couldn't reach the payer";

  const apt = r.appointment && r.appointment.aptDateTime;
  const lookahead = Number(a.lookaheadDays);
  if (!apt || !Number.isFinite(lookahead) || lookahead < 0) return null;

  const aptMs = Date.parse(String(apt).replace(' ', 'T'));
  if (!Number.isFinite(aptMs)) return null;

  const nowMs = now instanceof Date ? now.getTime() : (Number.isFinite(now) ? now : Date.now());
  const cutoff = new Date(nowMs);
  cutoff.setDate(cutoff.getDate() + lookahead);
  cutoff.setHours(23, 59, 59, 999);

  return aptMs <= cutoff.getTime()
    ? { text: `${lead} — will retry automatically tonight` }
    : { text: `${lead} — will retry automatically closer to the appointment` };
}

/**
 * Friendly copy for a /verify PRE-FLIGHT bailout (a 200 with data.ok=false and a
 * `reason`). Returns null for reasons we don't special-case (treated as a normal
 * verification result). These are the only reasons runEligibilityCheck sets.
 *
 * `data` is OPTIONAL and only consulted for `blocked_bad_data` (which quotes the
 * offending fields) and `payer_no_eligibility` (which names the payer). Every
 * existing one-argument call site keeps working unchanged; calling either with no
 * data still returns a sensible generic message rather than null or a throw.
 */
export function verifyReasonMessage(reason, data) {
  switch (reason) {
    case 'no_billing_provider':
      return 'Set a billing provider in Precheck settings to run verification.';
    // The billing provider has no NPI in Open Dental (2026-10-29). The 270 now sends the NPI only
    // and Stedi fills the name from it, so a provider without one is refused FREE instead of
    // paying for a guaranteed failure. Office-fixable: add the NPI in OD, then verify.
    case 'no_billing_npi':
      return 'The billing provider has no NPI in Open Dental. Add it to the provider in Open Dental, then verify.';
    case 'unmapped_carrier':
      return "This carrier isn't mapped to a payer yet — add a mapping in Precheck settings.";
    case 'no_active_insurance':
      return 'This patient has no active insurance on file to verify.';
    case 'blocked_bad_data':
      return blockedBadDataMessage(data);
    // The payer is known to Stedi but does not support 270/271 at all (backend
    // lib/eligibility-check.js STEP 2.5, keyed off the free Stedi payer directory).
    // Refused before any check ran, so — like blocked_bad_data — nothing was billed.
    case 'excluded_carrier':
      return excludedCarrierMessage(data);
    case 'payer_no_eligibility':
      return payerNoEligibilityMessage(data);
    default:
      return null;
  }
}

/**
 * Classify a /verify failure into { type, message, outcome }.
 *
 * `type` + `message` are what the dashboard renders (toast + drawer line). `outcome` is the
 * machine-readable why, which the Patient Profile panel uses to decide whether to wait for a
 * result (`unknown`) or tell the office plainly that nothing happened (`not_sent`).
 *
 * ⛔ "NO RESPONSE" IS TWO DIFFERENT THINGS, AND THIS USED TO CALL BOTH "STILL RUNNING".
 * Mint prod, 2026-09-17, PatNum 2741: the profile's /verify never reached AWS (zero Lambda
 * invocations, zero API Gateway errors), and the panel said "This check is still running —
 * refresh in a moment". Nothing was running. The office refreshed twice, found nothing, and
 * had to verify from the dashboard instead.
 *   - `err.request` present, no `err.response` (axios 0.18's network-error shape), or a
 *     504/502/408/ECONNABORTED → we genuinely do not know whether the check ran: the Lambda
 *     may still persist a result after API Gateway's 29s cap. → `unknown`.
 *   - no `err.request` at all → the request was never built: the store's own pre-send guard,
 *     or any synchronous throw. Nothing was sent and nothing was billed. → `not_sent`.
 *
 * `refused` = the store's post-response throws (tagged with `err.response` by verifyPatient):
 * the backend answered and declined, e.g. the feature flag is off.
 */
export function classifyVerifyError(err) {
  const resp = err && err.response;
  const status = resp && resp.status;
  const serverMsg = resp && resp.data
    ? (resp.data.message || (typeof resp.data === 'string' ? resp.data : null))
    : null;

  if (status === 403) {
    return { type: 'warning', outcome: 'disabled', message: serverMsg || "Verification isn't enabled for this practice." };
  }
  if (status === 200) {
    return {
      type: 'warning',
      outcome: 'refused',
      message: (err && err.message) || serverMsg || 'The check was not run.',
    };
  }
  if (status === 504 || status === 502 || status === 408
    || (err && err.code === 'ECONNABORTED')
    || (!resp && err && err.request)) {
    return {
      type: 'info',
      outcome: 'unknown',
      message: 'No answer from the check — it may still be finishing. Refresh in a moment; if nothing appears, run it again.',
    };
  }
  if (!resp) {
    return { type: 'warning', outcome: 'not_sent', message: 'The check was not sent — nothing was billed. Try again.' };
  }
  return { type: 'error', outcome: 'failed', message: serverMsg || 'Verification failed. Please try again.' };
}

/**
 * The Patient Profile panel's free follow-up after an `unknown` /verify outcome: re-read
 * /patient-status on this schedule and show the result the moment one lands.
 *
 * 45s total. That covers the on-demand budget (teamio-serverless-api
 * insurance-verification-service lib/worker-deps.js ON_DEMAND.TOTAL_BUDGET_MS = 24s) plus the
 * persist, and API Gateway's 29s cap — a check that ran at all has written its row well
 * inside this window.
 */
export const VERIFY_FOLLOW_UP_DELAYS_MS = [5000, 10000, 15000, 15000];

// After the LAST read is requested, how long to wait for its answer before saying "no result".
// Without it the verdict would land before the final read's response could.
export const VERIFY_FOLLOW_UP_GRACE_MS = 5000;

/**
 * The identity of one plan's latest stored check in a /patient-status payload, or null when
 * the plan has none. Keyed by ORDINAL, never "the patient's latest": IsLatest is per
 * (PatNum, PlanNum), and comparing across plans is the wrong-plan bug this feature has hit
 * three times.
 */
export function planVerificationMark(status, ordinal) {
  const plans = status && Array.isArray(status.plans) ? status.plans : [];
  const plan = plans.find((p) => p && Number(p.ordinal) === Number(ordinal));
  if (!plan) return null;
  const v = plan.verification || {};
  const verificationId = v.verificationId !== undefined && v.verificationId !== null && v.verificationId !== ''
    ? String(v.verificationId)
    : null;
  return {
    verificationId,
    checkedAt: v.checkedAt || null,
    lastVerified: plan.lastVerified || null,
  };
}

/**
 * Did a NEW check land for this plan between two /patient-status reads? A new id is the proof;
 * the timestamps are a fallback for a payload that carries no id. A plan that LOST its row
 * (id → null) is not a new result.
 */
export function hasNewVerification(before, after) {
  if (!after) return false;
  const b = before || {};
  if (after.verificationId) return after.verificationId !== (b.verificationId || null);
  if (b.verificationId) return false;
  if (after.checkedAt && after.checkedAt !== (b.checkedAt || null)) return true;
  return !!(after.lastVerified && after.lastVerified !== (b.lastVerified || null));
}

/**
 * Copy for a 200 `ok:false` /verify result that is TRANSIENT — the payer could not be reached,
 * or the interactive budget ran out before a 270 left. Null for anything else, so a real
 * payer answer keeps its own wording.
 *
 * ⛔ Delegates the "is it transient?" decision to isTransientFailure — the SAME rule the
 * dashboard's retry notice and the nightly runner use. A second rule here is how two surfaces
 * would end up disagreeing about one result.
 *
 * `carrierName` is the caller's fallback: /verify echoes `payerName` only for a
 * payer_no_eligibility refusal, and never the carrier, so the panel passes the plan's own.
 */
export function transientVerifyMessage(data, carrierName) {
  if (!data || !isTransientFailure(data)) return null;
  const payer = data.payerName || data.carrierName || carrierName || null;
  const lead = payer ? `Couldn't reach ${payer}` : "Couldn't reach the carrier";
  const tail = data.persisted === false ? ' No check was recorded.' : '';
  return `${lead} — try again in a few minutes.${tail}`;
}

/**
 * makeVerifyFollowUp(opts) → { start(marksBefore), notify(status) → bool, stop(), isActive() }
 *
 * The timer half of the panel's follow-up, kept out of the .vue file so node can test it.
 *   - start(marksBefore): marksBefore = { [ordinal]: planVerificationMark } for the plans the
 *     check covered. Each tick calls `refresh()` (the panel asks its parent for a quiet
 *     /patient-status read) and schedules the next delay; `graceMs` after the last one,
 *     `onExhausted()`.
 *   - notify(status): compare the fresh payload with marksBefore; on the first new result for
 *     any watched plan, stop and return true.
 *   - stop(): cancel. A token makes a tick already queued behind stop() a no-op, so a patient
 *     switch or an unmount can never fire a read for the wrong patient.
 *
 * ⛔ IT IS NEVER GIVEN A VERIFY FUNCTION. Every /verify is a billed check; this only reads.
 */
export function makeVerifyFollowUp(opts) {
  const o = opts || {};
  const delays = Array.isArray(o.delays) ? o.delays : VERIFY_FOLLOW_UP_DELAYS_MS;
  const graceMs = Number.isFinite(o.graceMs) ? o.graceMs : VERIFY_FOLLOW_UP_GRACE_MS;
  const refresh = typeof o.refresh === 'function' ? o.refresh : () => {};
  const onExhausted = typeof o.onExhausted === 'function' ? o.onExhausted : () => {};
  const setT = typeof o.setTimeoutFn === 'function' ? o.setTimeoutFn : (fn, ms) => setTimeout(fn, ms);
  const clearT = typeof o.clearTimeoutFn === 'function' ? o.clearTimeoutFn : (h) => clearTimeout(h);

  let token = 0;
  let timer = null;
  let active = false;
  let before = {};

  function stop() {
    token += 1;
    active = false;
    if (timer !== null) clearT(timer);
    timer = null;
  }

  function schedule(i, myToken) {
    if (i >= delays.length) {
      timer = setT(() => {
        if (myToken !== token) return;
        stop();
        onExhausted();
      }, graceMs);
      return;
    }
    timer = setT(() => {
      if (myToken !== token) return;
      refresh();
      schedule(i + 1, myToken);
    }, delays[i]);
  }

  return {
    start(marksBefore) {
      stop();
      before = marksBefore && typeof marksBefore === 'object' ? marksBefore : {};
      active = true;
      schedule(0, token);
    },
    notify(status) {
      if (!active) return false;
      const ordinals = Object.keys(before);
      const landed = ordinals.some((ord) => hasNewVerification(before[ord], planVerificationMark(status, ord)));
      if (landed) stop();
      return landed;
    },
    stop,
    isActive() { return active; },
  };
}

// ── Procedure codes (Precheck settings) ─────────────────────────────────────
//
// Moved here from PrecheckSettingsModal.vue 2026-08-17 so the list-building and
// validation logic is reachable from a node test — the modal is an SFC and cannot be
// require()'d, so anything left inside it can only ever be checked by regex over source.

// A CDT procedure code (D followed by 4 digits) + the office's list cap. Mirrors the
// backend CDT_CODE_RE + PROCEDURE_CODES_MAX in insurance-verification.js validateSettingsPatch.
export const CDT_CODE_RE = /^D\d{4}$/;
export const PROCEDURE_CODES_MAX = 50;

// FE MIRROR of insurance-verification-service/lib/procedure-codes.js
// DEFAULT_DENTAL_PROCEDURE_CODES — the Teamio default set shown as the starting point when
// an office hasn't customized its list. A browser SPA can't require() the sibling Node repo,
// so this is a maintained copy; keep it in lockstep with the backend source (which is pinned
// by the service's tests/procedure-codes.test.js and the admin seeder's cross-repo deep-equal).
export const DEFAULT_PROCEDURE_CODES = [
  // Diagnostic (exams + X-rays)
  'D0120', 'D0140', 'D0150', 'D0210', 'D0220', 'D0272', 'D0274', 'D0330',
  // Preventive (cleanings, fluoride, sealants)
  'D1110', 'D1120', 'D1206', 'D1208', 'D1351',
  // Restorative (fillings, crowns, buildup)
  'D2140', 'D2150', 'D2160', 'D2330', 'D2391', 'D2392', 'D2393', 'D2740', 'D2750', 'D2950',
  // Endodontics (root canals)
  'D3310', 'D3320', 'D3330',
  // Periodontics (scaling/root planing, maintenance)
  'D4341', 'D4342', 'D4910',
  // Prosthodontics (dentures + implants)
  'D5110', 'D5120', 'D5213', 'D5214', 'D6010', 'D6056', 'D6058',
  // Oral Surgery (extractions)
  'D7140', 'D7210',
  // Orthodontics
  'D8080',
  // Adjunctive (palliative, sedation, night guard)
  'D9110', 'D9230', 'D9944',
];

/**
 * normalizeCdtInput(raw) → 'D0120' | null
 *
 * The one place a code becomes canonical: trimmed, upper-cased, and required to be a real
 * CDT shape. Everything below funnels through it so the dropdown, the free-text box and the
 * stored-settings loader cannot disagree about what counts as a code.
 */
export function normalizeCdtInput(raw) {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toUpperCase();
  return CDT_CODE_RE.test(code) ? code : null;
}

/**
 * tryAddProcCode(list, raw) → { ok: true, code } | { ok: false, error }
 *
 * ⛔ The error strings are reproduced VERBATIM from the free-text editor this replaced. They
 * mirror the backend's validateSettingsPatch messages, and the node test pins them — if you
 * reword one here, the office starts seeing different wording than the API would return on
 * the same input.
 *
 * Never mutates `list`; the caller decides what to do with the accepted code.
 */
export function tryAddProcCode(list, raw) {
  const current = Array.isArray(list) ? list : [];
  const trimmed = typeof raw === 'string' ? raw.trim().toUpperCase() : '';
  if (!trimmed) return { ok: false, error: null };

  const code = normalizeCdtInput(trimmed);
  if (!code) {
    return { ok: false, error: `"${trimmed}" isn't a valid CDT code — use the format D#### (e.g. D0120).` };
  }
  if (current.indexOf(code) !== -1) {
    return { ok: false, error: `${code} is already in the list.` };
  }
  if (current.length >= PROCEDURE_CODES_MAX) {
    return { ok: false, error: `You can add at most ${PROCEDURE_CODES_MAX} codes.` };
  }
  return { ok: true, code: code };
}

/**
 * buildProcCodeOptions(odRows, selectedCodes) → [{ value, text, desc }]
 *
 * The dropdown's option list, merged from three sources and keyed on the CDT code.
 *
 * ⛔ `value` is ALWAYS the ProcCode, never the OD CodeNum. The consent-form picker this UI is
 * modelled on (TxPresenter/components/settings/SettingsConsent.vue) keys its options on
 * `String(c.CodeNum || c.ProcCode)`, so its saved blobs can hold "160". That is fine there —
 * it matches back against Open Dental. Here the codes are put on the wire in the X12 270
 * request, where a CodeNum is not a procedure and the payer would reject it. Copying that
 * mapping is the single most likely way to break this screen; a test asserts against it.
 *
 * Sources, first writer wins:
 *   1. the office's own OD codes (carry a real description)
 *   2. Teamio's default set — so an office whose /runquery failed still gets a usable list
 *   3. anything currently selected — so a selected row shows checked and can be toggled off
 */
export function buildProcCodeOptions(odRows, selectedCodes) {
  const byCode = new Map();

  (Array.isArray(odRows) ? odRows : []).forEach((row) => {
    if (!row || typeof row !== 'object') return;
    const code = normalizeCdtInput(row.ProcCode);
    // OD's procedurecode table carries the practice's custom and non-CDT codes too. They are
    // legitimate rows, they just cannot be asked about in a 270 — so they never become options.
    if (!code || byCode.has(code)) return;
    const desc = typeof row.Descript === 'string' ? row.Descript.trim() : '';
    byCode.set(code, { value: code, text: desc ? `${code} - ${desc}` : code, desc: desc });
  });

  DEFAULT_PROCEDURE_CODES.forEach((code) => {
    if (!byCode.has(code)) byCode.set(code, { value: code, text: code, desc: '' });
  });

  (Array.isArray(selectedCodes) ? selectedCodes : []).forEach((raw) => {
    const code = normalizeCdtInput(raw);
    if (code && !byCode.has(code)) byCode.set(code, { value: code, text: code, desc: '' });
  });

  return Array.from(byCode.values()).sort((a, b) => (a.value < b.value ? -1 : a.value > b.value ? 1 : 0));
}

/** Options keyed by code, for O(1) chip lookups. */
export function procOptionMapOf(options) {
  const map = {};
  (Array.isArray(options) ? options : []).forEach((o) => { if (o && o.value) map[o.value] = o; });
  return map;
}

/**
 * procOptionsForSelection(selectedCodes, optionMap) → [{ value, text, desc }]
 *
 * ⛔ This is the INVERSE of SettingsConsent's codeOptionsForRow, and the inversion is the
 * point. That helper FILTERS the stored codes against the loaded options, so a code the
 * office no longer carries in Open Dental silently vanishes from the row. Here a missing
 * code falls back to a bare option instead, so it still renders as a removable chip.
 * Losing a code from this list means quietly asking the payer about less than the office
 * asked for — and nothing on screen would say so.
 */
export function procOptionsForSelection(selectedCodes, optionMap) {
  const map = optionMap || {};
  return (Array.isArray(selectedCodes) ? selectedCodes : [])
    .map((code) => map[code] || { value: code, text: code, desc: '' });
}

/**
 * seedProcCodesState(stored) → { codes, customized }
 *
 * Resolves the saved `procedureCodes` setting into editor state. `null` / absent / empty /
 * all-invalid all mean "this office is on the Teamio defaults" — the backend stores null for
 * exactly that, so an office that deletes every chip is back on defaults rather than sending
 * an empty code set.
 */
export function seedProcCodesState(stored) {
  const cleaned = [];
  (Array.isArray(stored) ? stored : []).forEach((raw) => {
    const code = normalizeCdtInput(raw);
    if (code && cleaned.indexOf(code) === -1) cleaned.push(code);
  });
  return cleaned.length
    ? { codes: cleaned, customized: true }
    : { codes: DEFAULT_PROCEDURE_CODES.slice(), customized: false };
}

// ── Carrier pause banner (2026-09-18, backend migration 005) ────────────────────────────────
//
// A payer that rejects the practice's BILLING PROVIDER (provider_config — AAA 41/43/44/45/50/51/
// 97/T4) will reject every patient on that carrier the same way, and each check is billed. The
// nightly runner therefore PAUSES that carrier at the site until an admin clicks "Resume checks".
// The copy lives here, pure, so it is testable without mounting; the backend's Slack alert keeps
// its own wording (lib/carrier-pause.js pauseFixSteps) — a browser cannot require that file.

/**
 * carrierPauseView(pause, tz) → everything CarrierPauseBanner renders for one pause:
 *   { key, carrierNum, title, reason, since, heldBack, provider, steps[], confirm }
 * `pause` is one entry of the dashboard's `carrierPauses[]`.
 */
export function carrierPauseView(pause, tz) {
  const p = pause && typeof pause === 'object' ? pause : {};
  const carrier = (p.carrierName && String(p.carrierName).trim()) || 'this carrier';
  const payer = p.payerId ? `payer ${p.payerId}` : 'The payer';
  const code = p.errorCode ? `AAA ${p.errorCode}` : '';
  const detail = p.errorDetail ? `“${p.errorDetail}”` : '';
  const reasonTail = [code, detail].filter(Boolean).join(' ');
  const n = Number(p.upcomingCount);
  const bp = p.billingProvider && typeof p.billingProvider === 'object' ? p.billingProvider : null;
  return {
    key: `pause-${p.pauseId != null ? p.pauseId : p.carrierNum}`,
    carrierNum: p.carrierNum,
    title: `Automatic checks paused for ${carrier}`,
    reason: `${p.payerId ? `Payer ${p.payerId}` : 'The payer'} rejected the practice's billing provider${reasonTail ? ` — ${reasonTail}` : ''}.`,
    since: p.pausedAt ? formatCheckedAt(p.pausedAt, tz) : '',
    heldBack: Number.isFinite(n) && n > 0
      ? `${n} patient${n === 1 ? '' : 's'} on this carrier in the current range won't be checked automatically. Verify still works for one patient at a time.`
      : "Patients on this carrier won't be checked automatically. Verify still works for one patient at a time.",
    provider: bp
      ? [bp.name || `Provider #${bp.provNum}`, bp.npi ? `NPI ${bp.npi}` : 'no NPI on file', bp.isOrganization ? 'organization' : 'individual']
        .join(' · ')
      : '',
    steps: [
      `Confirm the billing provider's NPI is enrolled with ${payer.replace(/^The payer$/, 'this payer')} for eligibility (Medicare: complete CMS attestation).`,
      `Check ${carrier}'s Electronic ID in Open Dental${p.payerId ? ` (it resolves to ${p.payerId})` : ''} — a generic carrier can be mapped to the wrong payer, such as another state's Blue Cross Blue Shield.`,
      `Or exclude ${carrier} in Precheck settings and verify those patients by phone.`,
    ],
    confirm: `Every scheduled patient on ${carrier} will be checked again, and each check is billed. `
      + 'If the payer still rejects the billing provider, checks pause again after the first failure.',
  };
}

/**
 * "Change value of Appointment Field Defs" (2026-09-23) — FE twin of the backend's
 * sql/od-writeback.js apptFieldTextError, used on BOTH the FieldName and the value.
 * Returns null when acceptable, else a short reason. Plain text only: no `<`, `>` or control
 * characters; trimmed non-empty; at most APPT_FIELD_TEXT_MAX characters.
 * ⛔ Keep the rule identical to the backend's — a value the modal accepts but /save-settings
 * rejects is a save that fails after the modal has already closed (the save is optimistic).
 */
export const APPT_FIELD_TEXT_MAX = 255;
const APPT_FIELD_UNSAFE_RE = /[<>\u0000-\u001f\u007f]/;

export function apptFieldTextError(value) {
  if (typeof value !== 'string') return 'must be a string';
  const t = value.trim();
  if (!t) return 'must not be empty';
  if (t.length > APPT_FIELD_TEXT_MAX) return `must be at most ${APPT_FIELD_TEXT_MAX} characters`;
  if (APPT_FIELD_UNSAFE_RE.test(value)) return 'must be plain text (no <, > or control characters)';
  return null;
}
